import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, CheckCircle2, ChevronDown, CircleHelp, Copy, Info, Loader2, X, XCircle } from 'lucide-react';
import { characterFor } from './characters';
import { initialsOf, toneFor } from './avatarTones';
import { deltaInfo } from 'src/workspace/format';

// Stage building blocks for Meta Ads, matching auto-appy src/features/Workspace/WorkspaceUi.jsx.
// Pages compose these instead of writing one-off markup, so every screen feels the same.

export function StagePage({ children, wide = false, label, className = '' }) {
  return (
    <main className={`ws mx-stage ${className}`.trim()} aria-label={label}>
      <div className="ws-page" style={wide ? { maxWidth: 1480 } : undefined}>{children}</div>
    </main>
  );
}

/** Eyebrow, big title, one line of help, actions on the right. */
export function StageHeader({ eyebrow, title, description, children, below }) {
  return (
    <header className="ws-hero mx-hero">
      <div className="ws-grow">
        {eyebrow && <div className="ws-eyebrow">{eyebrow}</div>}
        <h1 className="ws-title" style={{ marginTop: eyebrow ? 6 : 0 }}>{title}</h1>
        {description && <p className="ws-muted mx-hero-copy">{description}</p>}
        {below}
      </div>
      {children && <div className="mx-hero-actions">{children}</div>}
    </header>
  );
}

export function Card({ title, subtitle, actions, children, pad = true, className = '', style, id }) {
  return (
    <section className={`ws-card${pad ? ' ws-card-pad' : ''} ${className}`.trim()} style={style} id={id}>
      {(title || actions) && (
        <div className="mx-card-head">
          <div className="ws-grow">
            {title && <h2 className="ws-h2">{title}</h2>}
            {subtitle && <p className="ws-muted" style={{ marginTop: 2 }}>{subtitle}</p>}
          </div>
          {actions && <div className="mx-card-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Monogram({ name = '', size = 32, seed }) {
  const tone = toneFor(seed ?? name);
  return <span className="ws-monogram" style={{ width: size, height: size, fontSize: Math.round(size * 0.36), background: tone.bg, color: tone.fg, '--mono-ring': tone.ring }} aria-hidden="true">{initialsOf(name) || 'AA'}</span>;
}

export function Character({ name = 'appy', size = 44, full = false }) {
  const character = characterFor(name);
  if (!character) return null;
  return (
    <span className="ws-avatar" style={{ width: size, height: size, borderRadius: Math.round(size * 0.3), background: character.tint }}>
      <img src={full && character.full ? character.full : character.image} alt="" loading="lazy" />
    </span>
  );
}

export function Status({ tone = 'draft', children, title }) {
  return <span className={`ws-status ws-status--${tone}`} title={title}>{children}</span>;
}

export function Skeleton({ width = '100%', height = 14, radius = 8, style }) {
  return <span className="ws-skeleton" style={{ display: 'block', width, height, borderRadius: radius, ...style }} aria-hidden="true" />;
}

export function LoadingCard({ lines = 3, height = 180 }) {
  return (
    <div className="ws-card ws-card-pad" aria-busy="true">
      <Skeleton width={160} height={14} />
      <Skeleton width="45%" height={26} style={{ marginTop: 10 }} />
      {lines > 2 && <Skeleton height={height} radius={16} style={{ marginTop: 16 }} />}
    </div>
  );
}

export function Spinner({ size = 16 }) {
  return <Loader2 size={size} className="ws-spin" aria-hidden="true" />;
}

const NOTICE_ICONS = { info: Info, warning: AlertTriangle, error: XCircle, success: CheckCircle2, tip: CircleHelp };

/** A coloured message strip. tone: info | warning | error | success | tip. */
export function Notice({ tone = 'info', title, children, action, onClose, compact = false }) {
  const Icon = NOTICE_ICONS[tone] || Info;
  return (
    <div className={`mx-notice mx-notice--${tone}${compact ? ' is-compact' : ''}`} role={tone === 'error' ? 'alert' : 'status'}>
      <Icon size={17} aria-hidden="true" className="mx-notice-icon" />
      <div className="ws-grow">
        {title && <strong>{title}</strong>}
        {children && <div className="mx-notice-body">{children}</div>}
      </div>
      {action}
      {onClose && <button type="button" className="mx-icon-btn" onClick={onClose} aria-label="Dismiss"><X size={15} /></button>}
    </div>
  );
}

/** Empty state with a character, a sentence and the next step. */
export function EmptyState({ character = 'appy', title, children, action, compact = false }) {
  return (
    <div className={`mx-empty${compact ? ' is-compact' : ''}`}>
      {character && <Character name={character} size={compact ? 52 : 72} />}
      <strong>{title}</strong>
      {children && <p>{children}</p>}
      {action && <div className="mx-empty-action">{action}</div>}
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <Notice tone="error" title="This did not load" action={onRetry && <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={onRetry}>Try again</button>}>
      {message}
    </Notice>
  );
}

/** "+12%" coloured by whether the change is good. */
export function Delta({ value, lowerIsBetter = false, suffix = 'vs previous' }) {
  const info = deltaInfo(value, { lowerIsBetter });
  if (!info) return <span className="mx-delta is-flat">No earlier data</span>;
  return (
    <span className={`mx-delta${info.good === true ? ' is-good' : info.good === false ? ' is-bad' : ' is-flat'}`} title={suffix}>
      {info.text}<span className="mx-delta-suffix"> {suffix}</span>
    </span>
  );
}

export function Kpi({ label, value, delta, lowerIsBetter, hint, help, icon: Icon, onClick, active }) {
  // A div with button behaviour, so the HelpTip button inside stays valid HTML.
  const interactive = onClick ? {
    role: 'button', tabIndex: 0, 'aria-pressed': Boolean(active), onClick,
    onKeyDown: (event) => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); onClick(); } },
  } : {};
  return (
    <div className={`mx-kpi${onClick ? ' is-button' : ''}${active ? ' is-active' : ''}`} {...interactive}>
      <span className="ws-kpi-label">{Icon && <Icon size={14} aria-hidden="true" />}{label}{help && <HelpTip text={help} />}</span>
      <span className="ws-kpi-value">{value}</span>
      {delta !== undefined && <Delta value={delta} lowerIsBetter={lowerIsBetter} />}
      {hint && <span className="ws-muted mx-kpi-hint">{hint}</span>}
    </div>
  );
}

/** A small "?" that explains a term in plain words. */
export function HelpTip({ text, label = 'What does this mean?' }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <span className="mx-help" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" className="mx-help-btn" aria-label={label} aria-describedby={open ? id : undefined}
        onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onClick={(event) => { event.preventDefault(); event.stopPropagation(); setOpen((v) => !v); }}>
        <CircleHelp size={13} aria-hidden="true" />
      </button>
      {open && <span role="tooltip" id={id} className="mx-help-tip">{text}</span>}
    </span>
  );
}

export function Field({ label, hint, error, children, htmlFor, optional, right }) {
  return (
    <div className={`mx-field${error ? ' has-error' : ''}`}>
      {(label || right) && (
        <div className="mx-field-top">
          {label && <label className="ws-label" htmlFor={htmlFor}>{label}{optional && <span className="mx-optional"> optional</span>}</label>}
          {right}
        </div>
      )}
      {children}
      {error ? <span className="mx-field-error" role="alert">{error}</span> : hint ? <span className="mx-field-hint">{hint}</span> : null}
    </div>
  );
}

export const TextInput = React.forwardRef(function TextInput({ prefix, suffix, className = '', value, ...rest }, ref) {
  const props = value === undefined ? rest : { ...rest, value: value ?? '' };
  if (!prefix && !suffix) return <input ref={ref} className={`mx-input ${className}`.trim()} {...props} />;
  return (
    <span className={`mx-input-wrap ${className}`.trim()}>
      {prefix && <span className="mx-input-affix">{prefix}</span>}
      <input ref={ref} className="mx-input mx-input--bare" {...props} />
      {suffix && <span className="mx-input-affix">{suffix}</span>}
    </span>
  );
});

export function TextArea({ className = '', counter, max, value, ...props }) {
  const text = value ?? '';
  return (
    <span className="mx-textarea-wrap">
      <textarea className={`mx-input mx-textarea ${className}`.trim()} value={text} maxLength={max} {...props} />
      {counter && <span className={`mx-counter${String(text).length > counter ? ' is-over' : ''}`}>{String(text).length} / {counter}</span>}
    </span>
  );
}

export function Toggle({ checked, onChange, label, hint, disabled, id }) {
  const autoId = useId();
  const inputId = id || autoId;
  return (
    <label className={`mx-toggle${disabled ? ' is-disabled' : ''}`} htmlFor={inputId}>
      <input id={inputId} type="checkbox" role="switch" checked={Boolean(checked)} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
      <span className="mx-toggle-track" aria-hidden="true"><span className="mx-toggle-thumb" /></span>
      {(label || hint) && (
        <span className="mx-toggle-text">
          {label && <span className="mx-toggle-label">{label}</span>}
          {hint && <span className="mx-toggle-hint">{hint}</span>}
        </span>
      )}
    </label>
  );
}

function CheckMark() {
  return <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

export function Checkbox({ checked, onChange, label, disabled, indeterminate = false, 'aria-label': ariaLabel }) {
  return (
    <label className={`mx-check${disabled ? ' is-disabled' : ''}${indeterminate ? ' is-mixed' : ''}`} onClick={(event) => event.stopPropagation()}>
      <input type="checkbox" checked={Boolean(checked)} disabled={disabled} aria-label={ariaLabel} onChange={(event) => onChange(event.target.checked)} />
      <span className="mx-check-box" aria-hidden="true">{indeterminate ? <span className="mx-check-dash" /> : <CheckMark />}</span>
      {label && <span>{label}</span>}
    </label>
  );
}

/** Big selectable cards (goal picker, formats, budget level). */
export function ChoiceCard({ selected, onSelect, icon: Icon, tone = 'blue', title, children, badge, disabled }) {
  return (
    <button type="button" className={`mx-choice${selected ? ' is-selected' : ''}`} onClick={onSelect} aria-pressed={Boolean(selected)} disabled={disabled}>
      {Icon && <span className={`mx-choice-icon mx-tone--${tone}`}><Icon size={19} aria-hidden="true" /></span>}
      <span className="ws-grow mx-choice-text">
        <span className="mx-choice-title">{title}{badge && <span className="mx-badge">{badge}</span>}</span>
        {children && <span className="mx-choice-body">{children}</span>}
      </span>
      <span className="mx-choice-check" aria-hidden="true">{selected && <CheckMark />}</span>
    </button>
  );
}

/** A modal dialog. Closes on Escape and backdrop click. */
export function Dialog({ open, onClose, title, description, children, footer, width = 520 }) {
  const ref = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const onKey = (event) => { if (event.key === 'Escape') closeRef.current?.(); };
    document.addEventListener('keydown', onKey);
    const timer = setTimeout(() => ref.current?.querySelector('input, textarea, .ws-sf, button:not(.mx-icon-btn)')?.focus(), 30);
    return () => { clearTimeout(timer); document.removeEventListener('keydown', onKey); previous?.focus?.(); };
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div className="ws-dialog-backdrop ws-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose?.(); }}>
      <div ref={ref} className="ws-dialog mx-dialog" role="dialog" aria-modal="true" aria-label={title} style={{ width: `min(${width}px, 100%)` }}>
        <div className="mx-dialog-head">
          <div className="ws-grow">
            <h2 className="ws-h2">{title}</h2>
            {description && <p className="ws-muted" style={{ marginTop: 4 }}>{description}</p>}
          </div>
          <button type="button" className="mx-icon-btn" onClick={onClose} aria-label="Close"><X size={17} /></button>
        </div>
        <div className="mx-dialog-body">{children}</div>
        {footer && <div className="mx-dialog-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({ open, title, children, confirmLabel = 'Confirm', danger = false, busy = false, onConfirm, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} title={title} width={440} footer={(
      <>
        <button type="button" className="ws-btn ws-btn--quiet" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className={`ws-btn ${danger ? 'ws-btn--danger' : 'ws-btn--primary'}`} onClick={onConfirm} disabled={busy}>{busy && <Spinner size={14} />}{confirmLabel}</button>
      </>
    )}>
      <div className="mx-confirm-text">{children}</div>
    </Dialog>
  );
}

/** A panel that slides in from the right (details, editors). */
export function Drawer({ open, onClose, title, subtitle, children, footer, width = 560, headerExtra }) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape' && !document.querySelector('.ws-dialog')) closeRef.current?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  if (!open) return null;
  return createPortal(
    <div className="mx-drawer-backdrop ws-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose?.(); }}>
      <aside className="mx-drawer" role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined} style={{ width: `min(${width}px, 100%)` }}>
        <div className="mx-drawer-head">
          <div className="ws-grow" style={{ minWidth: 0 }}>
            <h2 className="ws-h2 mx-ellipsis">{title}</h2>
            {subtitle && <div className="ws-muted" style={{ marginTop: 3 }}>{subtitle}</div>}
          </div>
          {headerExtra}
          <button type="button" className="mx-icon-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>
        <div className="mx-drawer-body">{children}</div>
        {footer && <div className="mx-drawer-foot">{footer}</div>}
      </aside>
    </div>,
    document.body,
  );
}

/** Underline tabs inside a card or page section. */
export function Tabs({ items, value, onChange, label }) {
  return (
    <div className="mx-tabs" role="tablist" aria-label={label}>
      {items.map((item) => (
        <button key={item.id} type="button" role="tab" aria-selected={value === item.id} className="mx-tab" onClick={() => onChange(item.id)}>
          {item.icon && <item.icon size={15} aria-hidden="true" />}
          {item.label}
          {item.count !== undefined && item.count !== null && <span className="mx-tab-count">{item.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** A small menu button (row actions, "More"). items: [{label, icon, onClick, danger, disabled, hint} | {separator: true}] */
export function ActionMenu({ items, label = 'More actions', icon: Icon, buttonClassName = 'ws-row-more', align = 'right', children }) {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const list = items.filter(Boolean);
  useEffect(() => {
    if (!open) return undefined;
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const height = Math.min(380, list.length * 40 + 16);
      const up = window.innerHeight - rect.bottom < height + 12 && rect.top > height;
      setPlace({ ...(align === 'right' ? { right: Math.max(8, window.innerWidth - rect.right) } : { left: Math.max(8, rect.left) }), ...(up ? { bottom: window.innerHeight - rect.top + 6 } : { top: rect.bottom + 6 }) });
    }
    const onPointer = (event) => { if (!menuRef.current?.contains(event.target) && !buttonRef.current?.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') { setOpen(false); buttonRef.current?.focus(); } };
    const onScroll = (event) => { if (!menuRef.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => { document.removeEventListener('mousedown', onPointer); document.removeEventListener('keydown', onKey); window.removeEventListener('scroll', onScroll, true); };
  }, [open, list.length, align]);
  return (
    <>
      <button ref={buttonRef} type="button" className={buttonClassName} aria-haspopup="menu" aria-expanded={open} aria-label={children ? undefined : label}
        onClick={(event) => { event.stopPropagation(); setOpen((v) => !v); }}>
        {children || (Icon ? <Icon size={16} /> : <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>⋯</span>)}
      </button>
      {open && place && createPortal(
        <div ref={menuRef} className="ws-overlay mx-menu" role="menu" style={place} onClick={(event) => event.stopPropagation()}>
          {list.map((item, index) => item.separator ? <div key={`sep${index}`} className="ws-menu-sep" /> : (
            <button key={`${item.label}${index}`} type="button" role="menuitem" className={`ws-menu-item${item.danger ? ' is-danger' : ''}`} disabled={item.disabled}
              onClick={() => { setOpen(false); item.onClick?.(); }}>
              {item.icon && <item.icon size={15} aria-hidden="true" />}
              <span className="ws-grow">{item.label}{item.hint && <span className="mx-menu-hint">{item.hint}</span>}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}

export function CopyButton({ value, label = 'Copy' }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={async () => {
      try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1600); } catch { /* clipboard blocked */ }
    }}>
      {done ? <Check size={14} /> : <Copy size={14} />}{done ? 'Copied' : label}
    </button>
  );
}

/** A disclosure section ("Advanced options"). */
export function Disclosure({ title, hint, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`mx-disclosure${open ? ' is-open' : ''}`}>
      <button type="button" className="mx-disclosure-head" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="ws-grow"><span className="mx-disclosure-title">{title}</span>{hint && <span className="ws-muted mx-disclosure-hint">{hint}</span>}</span>
        <ChevronDown size={16} aria-hidden="true" className="mx-disclosure-chev" />
      </button>
      {open && <div className="mx-disclosure-body">{children}</div>}
    </div>
  );
}

/** Meta delivery label: tone from the backend (live, info, warning, paused, failed, draft). */
export function Delivery({ delivery }) {
  if (!delivery) return <Status tone="draft">Unknown</Status>;
  return <Status tone={delivery.tone} title={delivery.hint}>{delivery.label}</Status>;
}

export const firstName = (name = '') => String(name).trim().split(/\s+/)[0] || '';

export function greeting(date = new Date()) {
  const hour = date.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}
